import { useEffect } from "react";

export const IFRAME_RESIZE_MESSAGE_TYPE = "pcna:iframe-resize";
export const IFRAME_RESIZE_MESSAGE_VERSION = 1;

export const isPageInsideIframe = (currentWindow = window) =>
  currentWindow.parent !== currentWindow;

export const shouldHideFramerEmbedChrome = (
  allowFramerEmbed,
  currentWindow = window,
) => allowFramerEmbed === true && isPageInsideIframe(currentWindow);

export const getFramerEmbedContentHeight = (element) => {
  if (!element) return 0;

  const boundingHeight = element.getBoundingClientRect?.().height || 0;

  return Math.ceil(
    Math.max(element.scrollHeight || 0, element.offsetHeight || 0, boundingHeight),
  );
};

export const getFramerEmbedParentOrigin = (referrer) => {
  if (!referrer) return "*";

  try {
    return new URL(referrer).origin;
  } catch (error) {
    return "*";
  }
};

export const postFramerEmbedHeight = ({
  currentWindow = window,
  currentDocument = document,
  parentWindow = currentWindow.parent,
  target = currentDocument.getElementById("main-layout"),
} = {}) => {
  if (!target || parentWindow === currentWindow) return 0;

  const height = getFramerEmbedContentHeight(target);
  if (!height) return 0;

  parentWindow.postMessage(
    {
      type: IFRAME_RESIZE_MESSAGE_TYPE,
      version: IFRAME_RESIZE_MESSAGE_VERSION,
      height,
    },
    getFramerEmbedParentOrigin(currentDocument.referrer),
  );

  return height;
};

const useFramerEmbedAutoResize = (enabled) => {
  useEffect(() => {
    if (!enabled || !isPageInsideIframe()) return undefined;

    const target = document.getElementById("main-layout");
    if (!target) return undefined;

    let lastHeight = 0;
    let stopped = false;

    const notifyParent = () => {
      if (stopped) return;

      const height = getFramerEmbedContentHeight(target);
      if (!height || height === lastHeight) return;

      lastHeight = postFramerEmbedHeight({ target });
    };

    let contentObserver;
    if (typeof window.ResizeObserver === "function") {
      contentObserver = new window.ResizeObserver(notifyParent);
      contentObserver.observe(target);
    } else if (typeof window.MutationObserver === "function") {
      contentObserver = new window.MutationObserver(notifyParent);
      contentObserver.observe(target, {
        attributes: true,
        childList: true,
        subtree: true,
      });
    }

    window.addEventListener("load", notifyParent);
    window.addEventListener("resize", notifyParent);
    document.fonts?.ready?.then(notifyParent).catch(() => {});
    notifyParent();

    return () => {
      stopped = true;
      contentObserver?.disconnect();
      window.removeEventListener("load", notifyParent);
      window.removeEventListener("resize", notifyParent);
    };
  }, [enabled]);
};

export default useFramerEmbedAutoResize;
